import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Linkedin, Twitter, Instagram, Facebook, Send, Loader2 } from 'lucide-react';
import { SiSnapchat } from 'react-icons/si';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { getLocalizedPath } from '@/lib/routes';
import { TurnstileWidget, TURNSTILE_SITE_KEY } from '@/components/TurnstileWidget';
import logo from '@/assets/logo-mufeed-nav.png';

const socialLinks = [
  { icon: Facebook, href: 'https://www.facebook.com/mufeed04', label: 'Facebook' },
  { icon: Twitter, href: 'https://x.com/mufeed_ksa', label: 'X' },
  { icon: Instagram, href: 'https://www.instagram.com/mufeed_ksa', label: 'Instagram' },
  { icon: Linkedin, href: 'https://www.linkedin.com/company/mufeed4org2008', label: 'LinkedIn' },
  { icon: SiSnapchat, href: 'https://www.snapchat.com/add/mufeed_sn', label: 'Snapchat' },
];

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const { toast } = useToast();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const homeBase = getLocalizedPath('home', lang);
  const hashBase = homeBase === '/' ? '' : homeBase;

  const footerLinks = [
    { label: t('footer.links.about'), href: getLocalizedPath('about', lang) },
    { label: t('footer.links.companies'), href: getLocalizedPath('companies', lang) },
    { label: t('footer.links.projects'), href: getLocalizedPath('projects', lang) },
    { label: t('footer.links.investors'), href: getLocalizedPath('investors', lang) },
    { label: t('footer.links.contact'), href: getLocalizedPath('contact', lang) },
    { label: t('footer.links.suppliers'), href: getLocalizedPath('suppliers', lang) },
    { label: t('footer.links.careers'), href: getLocalizedPath('careers', lang) },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast({
        title: t('footer.newsletter.errorTitle'),
        description: t('footer.newsletter.captchaMessage'),
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, website: honeypot, turnstileToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast({
        title: t('footer.newsletter.successTitle'),
        description: t('footer.newsletter.successMessage'),
      });
      setEmail('');
    } catch (err: any) {
      toast({
        title: t('footer.newsletter.errorTitle'),
        description:
          err?.message === 'captcha_failed'
            ? t('footer.newsletter.captchaMessage')
            : t('footer.newsletter.errorMessage'),
        variant: 'destructive',
      });
    } finally {
      // Turnstile tokens are single-use: reset for a fresh token after every attempt.
      setTurnstileReset((n) => n + 1);
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-card relative overflow-hidden">
      <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent" />

      <div className="py-16">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to={homeBase} className="inline-block mb-4">
                <motion.img 
                  src={logo} 
                  alt="Mufeed" 
                  className="h-10 w-auto"
                  whileHover={{ scale: 1.05 }}
                />
              </Link>
              <div className="flex gap-3 mt-6">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-muted-foreground hover:text-primary transition-all duration-300"
                    aria-label={social.label}
                    whileHover={{ 
                      scale: 1.1, 
                      boxShadow: '0 0 20px hsl(290 50% 55% / 0.4)',
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="w-4 h-4" />
                  </motion.a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-bold text-foreground mb-4 uppercase tracking-wider">{t('footer.quickLinks')}</h4>
              <nav className="flex flex-col gap-3">
                {footerLinks.map((link, index) => (
                  <motion.div
                    key={link.label}
                    whileHover={{ x: 5 }}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors w-fit"
                      data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
            </div>

            <div>
              <h4 className="font-bold text-foreground mb-4 uppercase tracking-wider">{t('footer.newsletter.title')}</h4>
              <p className="text-muted-foreground text-sm mb-4">
                {t('footer.newsletter.subtitle')}
              </p>
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.newsletter.placeholder')}
                  data-testid="input-newsletter-email"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:shadow-[0_0_15px_hsl(290_50%_55%_/_0.2)] transition-all duration-300 disabled:opacity-50"
                />
                <motion.button 
                  type="submit" 
                  disabled={isSubmitting}
                  data-testid="button-newsletter-submit"
                  className="px-4 py-3 bg-primary text-primary-foreground font-bold rounded-lg disabled:opacity-50"
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: '0 0 20px hsl(290 50% 55% / 0.5)',
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </motion.button>
              </form>
              {/* Honeypot: hidden from real users, bots tend to fill every field */}
              <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                <label htmlFor="newsletter-website">Website</label>
                <input
                  id="newsletter-website"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>
              <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileReset} />
            </div>
          </div>

          <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm uppercase tracking-wider">
              {t('footer.copyright')}
            </p>
            <div className="flex gap-6">
              <motion.div whileHover={{ y: -2 }}>
                <Link
                  to={getLocalizedPath('privacy', i18n.language === 'ar' ? 'ar' : 'en')}
                  className="text-muted-foreground text-sm hover:text-primary transition-colors uppercase tracking-wider"
                  data-testid="link-privacy-policy"
                >
                  {t('footer.privacy')}
                </Link>
              </motion.div>
              <span className="text-muted-foreground/50">|</span>
              <motion.div whileHover={{ y: -2 }}>
                <Link
                  to={getLocalizedPath('terms', i18n.language === 'ar' ? 'ar' : 'en')}
                  className="text-muted-foreground text-sm hover:text-primary transition-colors uppercase tracking-wider"
                  data-testid="link-terms-conditions"
                >
                  {t('footer.terms')}
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
